/**
 * Customers Client
 * Customer search and retrieval operations with PII masking
 */

import type { OperatorCustomer, CustomerSearchFilters, CustomerListResponse } from './types';
import { API_URL } from './constants';
import { createHeaders, getCurrentOperator, handleApiError, logApiCall } from './base-client';

// =============================================================================
// Customer Operations
// =============================================================================

/**
 * Search customers with role-based PII masking
 * Uses shared customers-service that derives customer data from bookings.
 * Backend applies PII masking based on operator role.
 *
 * @param filters - Search filters
 * @returns Paginated customer list
 *
 * @example
 * ```ts
 * const { customers, total } = await searchOperatorCustomers({
 *   query: 'Maria',
 *   businessId: 'biz-001',
 *   page: 1,
 *   limit: 20
 * });
 * ```
 */
export async function searchOperatorCustomers(
  filters?: CustomerSearchFilters
): Promise<CustomerListResponse> {
  const startTime = Date.now();
  const defaultResponse: CustomerListResponse = {
    customers: [],
    total: 0,
    page: 1,
    limit: 20,
  };

  // Always use API - mock data removed to prevent client bundling issues
  try {
    const params = new URLSearchParams();

    if (filters?.query) params.append('query', filters.query);
    if (filters?.businessId) params.append('businessId', filters.businessId);
    params.append('page', String(filters?.page || 1));
    params.append('limit', String(filters?.limit || 20));

    const response = await fetch(
      `${API_URL}/api/admin/customers/search?${params.toString()}`,
      { headers: createHeaders() }
    );

    logApiCall('/api/admin/customers/search', 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      if (response.status === 403) {
        return defaultResponse;
      }
      throw new Error(`API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    handleApiError(error, 'searchOperatorCustomers');
    return defaultResponse;
  }
}

/**
 * Get customer by ID
 *
 * @param customerId - Customer ID to retrieve
 * @returns Customer or null if not found
 */
export async function getCustomerById(customerId: string): Promise<OperatorCustomer | null> {
  const startTime = Date.now();
  const endpoint = `/api/admin/customers/${customerId}`;

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      headers: createHeaders(),
    });

    logApiCall(endpoint, 'GET', Date.now() - startTime, response.status);

    if (!response.ok) {
      if (response.status === 404 || response.status === 403) {
        return null;
      }
      throw new Error(`API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    handleApiError(error, 'getCustomerById', { customerId });
    return null;
  }
}

/**
 * Get customers for a specific business
 *
 * @param businessId - Business ID
 * @param page - Page number
 * @param limit - Items per page
 * @returns Paginated customer list for the business
 */
export async function getBusinessCustomers(
  businessId: string,
  page = 1,
  limit = 20
): Promise<CustomerListResponse> {
  return searchOperatorCustomers({ businessId, page, limit });
}

/**
 * Search customers by name or phone
 *
 * @param query - Search query (name or phone)
 * @param businessId - Optional business ID filter
 * @returns Matching customers
 */
export async function searchCustomers(
  query: string,
  businessId?: string
): Promise<CustomerListResponse> {
  return searchOperatorCustomers({ query, businessId });
}
